import { el } from '../../lib/dom'
import { SPEED_CHOICES, formatClock, speedLabel, type SpeedChoice } from './replayState'

export type PlayerControlsOptions = {
  readonly totalMs: () => number
  readonly onToggle: () => void
  readonly onRestart: () => void
  readonly onScrub: (ms: number) => void
  readonly onSpeed: (choice: SpeedChoice) => void
}

export type PlayerControls = {
  readonly element: HTMLElement
  readonly setPlaying: (playing: boolean) => void
  readonly setTime: (elapsedMs: number, totalMs: number) => void
}

export const createPlayerControls = (options: PlayerControlsOptions): PlayerControls => {
  const toggle = el('button', {
    class: 'btn btn-primary',
    type: 'button',
    onClick: options.onToggle,
  })
  const restart = el(
    'button',
    { class: 'btn', type: 'button', onClick: options.onRestart, 'aria-label': 'restart replay' },
    '↺',
  )
  const scrub = el('input', {
    type: 'range',
    class: 'scrub',
    min: 0,
    max: Math.max(1, Math.round(options.totalMs())),
    value: 0,
    step: 1,
    'aria-label': 'scrub replay',
    onInput: (event) => options.onScrub(Number((event.target as HTMLInputElement).value)),
  })
  const speed = el(
    'select',
    {
      class: 'speed',
      'aria-label': 'replay speed',
      onChange: (event) =>
        options.onSpeed((event.target as HTMLSelectElement).value as SpeedChoice),
    },
    ...SPEED_CHOICES.map((choice) => el('option', { value: choice }, speedLabel(choice))),
  )
  const time = el('span', { class: 'time' }, '0:00 / 0:00')
  const element = el('div', { class: 'controls' }, toggle, restart, scrub, time, speed)
  return {
    element,
    setPlaying: (playing) => {
      toggle.textContent = playing ? 'pause' : 'play'
      toggle.setAttribute('aria-pressed', String(playing))
    },
    setTime: (elapsed, total) => {
      const max = String(Math.max(1, Math.round(total)))
      const value = String(Math.round(elapsed))
      const label = `${formatClock(elapsed)} / ${formatClock(total)}`
      // Called every frame; only touch the DOM when something moved.
      if (scrub.max !== max) scrub.max = max
      if (scrub.value !== value) scrub.value = value
      if (time.textContent !== label) time.textContent = label
    },
  }
}
